import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, put, takeEvery } from 'redux-saga/effects'

import { ApiBusinessUnit, ApiClient, ApiNote, ApiTradingAccount } from '@/api/_legacy/generated-api'
import { createLogoutExtraReducers } from '@/utilities/redux'

type NoteType = 'new' | 'existing'

export type NoteStepMoveNextPayload = {
  businessUnit: ApiBusinessUnit
  client: ApiClient
  note: ApiNote
}

export type NoteStepState = {
  businessUnit?: ApiBusinessUnit
  client?: ApiClient
  dataErrorText?: string
  isDataError: boolean
  note?: ApiNote
  noteType?: NoteType
}

const initialState: NoteStepState = {
  isDataError: false
}

export const noteStepSlice = createSlice({
  name: 'noteStep',
  initialState,
  reducers: {
    init: () => {
      // state.note.client = undefined;
    },
    chooseNewNote: state => {
      state.noteType = 'new'
    },
    chooseExistingNote: state => {
      state.noteType = 'existing'
    },
    setNote: (state, action: PayloadAction<ApiNote>) => {
      state.note = action.payload
    },
    setClient: (state, action: PayloadAction<ApiClient>) => {
      state.client = action.payload
    },
    setBusinessUnit: (state, action: PayloadAction<ApiBusinessUnit>) => {
      state.businessUnit = action.payload
    },
    raiseDataError: (state, action: PayloadAction<string>) => {
      state.isDataError = true
      state.dataErrorText = action.payload
    },
    moveNext: (state, action: PayloadAction<NoteStepMoveNextPayload>) => {
      state.client = action.payload.client
      state.businessUnit = action.payload.businessUnit
    },
    reset: () => initialState
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

type LoadedState = {
  account: ApiTradingAccount
  businessUnit: ApiBusinessUnit
  client: ApiClient
  note: ApiNote
}

const loadState = (state: LoadedState) => async dispatch => {
  dispatch(noteStepSlice.actions.setNote(state.note))
  dispatch(noteStepSlice.actions.setClient(state.client))
  dispatch(noteStepSlice.actions.setBusinessUnit(state.businessUnit))
  dispatch(noteStepSlice.actions.chooseExistingNote())
}

const moveNext = (
  note: ApiNote,
  client: ApiClient,
  businessUnit: ApiBusinessUnit
): PayloadAction<NoteStepMoveNextPayload> => ({
  type: 'noteStep/moveNext',
  payload: {
    note,
    client,
    businessUnit
  }
})

export const actions = { ...noteStepSlice.actions, loadState, moveNext }

export type NoteActions = typeof actions

type NoteDetails = {
  businessUnit: ApiBusinessUnit
  client: ApiClient
  note: ApiNote
}

function* setNewlyCreatedNoteAndConvertToStepToExistingNoteSaga(
  action: PayloadAction<NoteDetails>
) {
  const { businessUnit, client, note } = action.payload

  yield put(actions.setNote(note))
  yield put(actions.moveNext(note, client, businessUnit))
  yield put(actions.chooseExistingNote())
}

function* setNoteAndMoveNext(action: PayloadAction<NoteDetails>) {
  const { businessUnit, client, note } = action.payload
  yield put(actions.setNote(note))
  yield put(actions.setClient(client))
  yield put(actions.setBusinessUnit(businessUnit))

  yield put(moveNext(note, client, businessUnit))
}

export function* noteStepRootSaga() {
  yield all([
    takeEvery('newNote/moveNext', setNewlyCreatedNoteAndConvertToStepToExistingNoteSaga),
    takeEvery('existingNote/moveNext', setNoteAndMoveNext)
  ])
}

export default noteStepSlice.reducer
